/**
 * Ably Real-Time Messaging Service
 * 
 * Handles real-time communication with desktop agents
 * Used for agent status updates and command responses
 */

import * as Ably from 'ably';

class AblyService {
  constructor() {
    this.client = null;
    this.channels = new Map();
    this.isInitialized = false;
    this.tokenFetcher = null;
    this.refreshTimer = null;
  }

  /**
   * Initialize Ably client with token
   * 
   * @param {string} token - Ably token (ephemeral)
   * @param {function} tokenFetcher - Function to fetch new tokens (optional, for auto-refresh)
   * @param {function} onConnected - Called once the underlying connection actually reaches 'connected' (optional)
   */
  initialize(token, tokenFetcher = null, onConnected = null) {
    if (this.isInitialized) {
      console.warn('Ably already initialized');
      return;
    }

    this.tokenFetcher = tokenFetcher;

    try {
      this.client = new Ably.Realtime({
        token: token, // Use token-based authentication
        authCallback: tokenFetcher ? (tokenParams, callback) => {
          // Auto-refresh tokens when needed
          tokenFetcher()
            .then(newToken => callback(null, newToken))
            .catch(error => callback(error, null));
        } : undefined,
        recover: (lastConnectionDetails, cb) => {
          // Attempt to recover connection
          cb(true);
        }
      });

      // Setup connection event listeners
      this.client.connection.on('connected', () => {
        this.isInitialized = true;

        // Setup token refresh (refresh 10 minutes before expiry, i.e., at 50 minutes)
        if (tokenFetcher) {
          this.setupTokenRefresh();
        }

        onConnected?.();
      });

      this.client.connection.on('disconnected', () => {
        console.log('Ably disconnected');
      });

      this.client.connection.on('suspended', () => {
        console.warn('Ably connection suspended');
      });

      this.client.connection.on('failed', (error) => {
        console.error('Ably connection failed:', error);
        this.isInitialized = false;
      });

      // Note: Token updates are handled via authCallback and setupTokenRefresh()
      // Ably auth object does not have an 'on' method for event listening

    } catch (error) {
      console.error('Failed to initialize Ably:', error);
      throw new Error('Failed to initialize real-time messaging');
    }
  }

  /**
   * Setup automatic token refresh
   * Refreshes token 10 minutes before expiration (at 50 minutes for 1-hour tokens)
   */
  setupTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh after 50 minutes (10 minutes before 1-hour expiry)
    const refreshInterval = 50 * 60 * 1000; // 50 minutes in milliseconds
    
    this.refreshTimer = setTimeout(async () => {
      try {
        console.log('Refreshing Ably token...');
        if (this.tokenFetcher) {
          const newToken = await this.tokenFetcher();
          await this.refreshToken(newToken);
        }
      } catch (error) {
        console.error('Failed to refresh Ably token:', error);
      }
    }, refreshInterval);
  }

  /**
   * Manually refresh the Ably token
   * 
   * @param {string} newToken - New Ably token
   */
  async refreshToken(newToken) {
    if (!this.client) {
      throw new Error('Ably client not initialized');
    }

    try {
      // Update the client with new token
      await this.client.auth.authorize({ token: newToken });
      console.log('Ably token refreshed successfully');
      
      // Setup next refresh
      if (this.tokenFetcher) {
        this.setupTokenRefresh();
      }
    } catch (error) {
      console.error('Failed to refresh Ably token:', error);
      throw error;
    }
  }

  /**
   * Re-authorize the connection with a freshly minted token.
   *
   * Tokens are scoped to the caller's stations at mint time, so after a new
   * station is linked the current token cannot attach to its channel. Calling
   * this before subscribing makes the new station's channel reachable.
   *
   * @returns {Promise<boolean>} true when a new token was obtained and applied
   */
  async reauthorize() {
    if (!this.client || !this.tokenFetcher) {
      return false;
    }
    await this.client.auth.authorize();
    return true;
  }

  /**
   * Subscribe to an agent's response channel
   *
   * @param {string} agentId - Agent UUID
   * @param {function} callback - Callback function for messages
   * @returns {object} Channel object
   */
  subscribeToAgent(agentId, callback) {
    if (!this.isInitialized || !this.client) {
      console.error('Ably not initialized');
      return null;
    }

    const channelName = `turbosync:agent-${agentId}:response`;
    
    // Return existing channel if already subscribed
    if (this.channels.has(channelName)) {
      return this.channels.get(channelName);
    }

    try {
      const channel = this.client.channels.get(channelName);
      
      // Subscribe to all messages. The attach can be refused (Ably 40160) when
      // the token's capability does not cover this channel, e.g. a station row
      // that is not a real station; that must not surface as an unhandled rejection.
      Promise.resolve(channel.subscribe((message) => {
        console.log(`Message from agent ${agentId}:`, message.data);
        callback(message);
      })).catch((error) => {
        console.warn(`Not subscribed to ${channelName}:`, error?.message ?? error);
      });

      // Store channel reference
      this.channels.set(channelName, channel);

      return channel;

    } catch (error) {
      console.error(`====>>>Failed to subscribe to ${channelName}:`, error);
      return null;
    }
  }

  /**
   * Unsubscribe from an agent's response channel
   * 
   * @param {string} agentId - Agent UUID
   */
  unsubscribeFromAgent(agentId) {
    const channelName = `turbosync:agent-${agentId}:response`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      try {
        channel.unsubscribe();
        channel.detach();
        this.channels.delete(channelName);
      } catch (error) {
        console.error(`Failed to unsubscribe from ${channelName}:`, error);
      }
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    this.channels.forEach((channel, channelName) => {
      try {
        channel.unsubscribe();
        channel.detach();
      } catch (error) {
        console.error(`Failed to unsubscribe from ${channelName}:`, error);
      }
    });
    this.channels.clear();
  }

  /**
   * Check if connected to Ably
   * 
   * @returns {boolean} Connection status
   */
  isConnected() {
    return this.client && this.client.connection.state === 'connected';
  }

  /**
   * Get connection state
   * 
   * @returns {string} Connection state
   */
  getConnectionState() {
    return this.client ? this.client.connection.state : 'not_initialized';
  }

  /**
   * Disconnect from Ably and clean up
   */
  disconnect() {
    if (!this.client) {
      return;
    }

    try {
      // Clear refresh timer
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }

      // Unsubscribe from all channels
      this.unsubscribeAll();

      // Close connection
      this.client.close();
      this.client = null;
      this.tokenFetcher = null;
      this.isInitialized = false;

      console.log('Ably disconnected and cleaned up');
    } catch (error) {
      console.error('Error during Ably disconnect:', error);
    }
  }
}

// Export singleton instance
export default new AblyService();



